import { zodResolver } from '@hookform/resolvers/zod'
import { createContext, useContext, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { PayloadError } from '@/constants/payload-error'
import { useCreateProject } from '@/hooks/mutations/use-create-project'
import { useEditProject } from '@/hooks/mutations/use-edit-project'
import { useGetAddressStates } from '@/hooks/queries/use-get-address-states'
import { useGetProjectByID } from '@/hooks/queries/use-get-project-by-id'
import { routes } from '@/routes/config'
import { GeoJSONObject } from '@/types/geospatial'
import { makeGenericZodErrorMessage } from '@/utils/make-generic-zod-error-message'

import { DEFAULT_COLUMN } from '../constants'
import { useIsEditPage } from '../hooks/use-is-edit-page'

const schema = z.object({
  project_name: z
    .string()
    .trim()
    .min(1, { message: 'O nome do projeto é obrigatório!' }),
  state: z
    .object(
      {
        name: z
          .string()
          .trim()
          .min(1, { message: 'A seleção do estado é obrigatória!' }),
        uf: z.string(),
      },
      makeGenericZodErrorMessage('A seleção do estado é obrigatória!'),
    )
    .refine((val) => !!val.name, {
      message: 'A seleção do estado é obrigatória!',
    }),
  city: z
    .string()
    .trim()
    .min(1, { message: 'A seleção da cidade é obrigatória!' }),
  regions: z.array(
    z
      .object({
        id: z.number().optional(),
        name: z
          .string()
          .trim()
          .min(1, { message: 'O nome da região é obrigatório!' }),
        geojson: z.custom<GeoJSONObject>().optional(),
        col_name: z.string().nullish().default(DEFAULT_COLUMN),
        removed: z.boolean().optional(),
        removed_features: z.array(z.object({ id: z.string() })).default([]),
        synced: z.boolean().optional(),
        is_new_file: z.boolean().optional(),
      })
      .superRefine((value, ctx) => {
        const geojsonSchema = z
          .object({
            features: z.array(
              z.object({
                properties: z.object({
                  [value.col_name || DEFAULT_COLUMN]: z.coerce
                    .string()
                    .trim()
                    .min(1, { message: 'Campo obrigatório!' }),
                }),
              }),
            ),
          })
          .optional()

        const result = geojsonSchema.safeParse(value.geojson)
        if (result.error) {
          ctx.addIssue({
            code: 'custom',
            path: ['geojson'],
            message: `Região ${value.name} está com o geojson inválido!`,
          })
          result.error.errors.map((error) =>
            ctx.addIssue({
              code: 'custom',
              path: ['geojson', ...error.path],
              message: 'Campo obrigatório!',
            }),
          )
        }
      }),
  ),
})

export type DefaultProjectFormData = z.infer<typeof schema>

function useProjectContext() {
  const { data: states = [] } = useGetAddressStates()

  const [activeStep, setActiveStep] = useState(1)
  const [activeRegionName, setActiveRegionName] = useState('')
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false)

  const { isEdit: isEditProjectPage, projectId: projectIdFromPage } =
    useIsEditPage()
  const navigate = useNavigate()

  const methods = useForm<DefaultProjectFormData>({
    defaultValues: {
      regions: [],
    },
    resolver: zodResolver(schema),
  })
  const { trigger, getValues, handleSubmit, reset, setValue } = methods

  const createProjectMutation = useCreateProject()
  const editProjectMutation = useEditProject(projectIdFromPage)
  const getProjectByIDQuery = useGetProjectByID(projectIdFromPage, {
    onSuccess: async (data) => {
      const state = states.find((value) => value.name === data.address.state)
      reset({
        project_name: data?.name,
        state: { name: state?.name, uf: state?.uf },
        city: data?.address.city,
        regions: data?.regions,
      })
    },
    onError: () => {
      navigate(-1)
      toast('Ocorreu um erro ao carregar o projeto', {
        type: 'error',
      })
    },
  })

  function goToFirstStep() {
    setActiveStep(1)
  }

  async function goToSecondStep() {
    const isValidBasicInfo = await trigger(['project_name', 'state', 'city'])
    const isValidRegions = await trigger('regions')

    const isRegionNameError = methods.formState.errors.regions?.some?.(
      (e) => e?.name,
    )

    if (!isValidBasicInfo || (!isValidRegions && isRegionNameError)) return

    const regions = getRegionsNotRemoved()

    const withRegions = regions.length

    if (withRegions) {
      const isExistActiveRegion =
        activeRegionName &&
        regions.some((region) => region.name === activeRegionName)

      if (!isExistActiveRegion) setActiveRegionName(regions[0].name)

      setActiveStep(2)
    } else {
      const { name } = createDefaultRegion()
      setActiveRegionName(name)
      setActiveStep(2)
    }
  }

  function goToBackRegion(index: number) {
    const backRegion = getRegionsNotRemoved()[index - 1]

    if (backRegion) {
      setActiveRegionName(backRegion.name)
    } else {
      setActiveStep(1)
    }
  }

  function goToNextRegion(index: number) {
    const nextRegion = getRegionsNotRemoved()[index + 1]

    if (nextRegion) {
      setActiveRegionName(nextRegion.name)
    } else {
      handleSubmit(handleForm)()
    }
  }

  function handleForm(data: DefaultProjectFormData) {
    if (!getRegionsNotRemoved(data.regions).length) {
      createDefaultRegion()
      data = getValues()
    }

    if (isEditProjectPage)
      return editProjectMutation.mutate(data, {
        onSuccess: () => setActiveStep(3),
        onError: () => setIsAlertModalOpen(true),
      })

    createProjectMutation.mutate(data, {
      onSuccess: () => setActiveStep(3),
      onError: () => setIsAlertModalOpen(true),
    })
  }

  function closeAlertModal() {
    setIsAlertModalOpen(false)
    if (createProjectMutation.error instanceof PayloadError) {
      goToFirstStep()
      navigate(routes.ProjectEdit(createProjectMutation.error.payload.id))
    }
  }

  function getRegionsNotRemoved(data = getValues('regions')) {
    return data.filter((region) => !(region.id && region.removed))
  }

  function createDefaultRegion() {
    const defaultRegion: DefaultProjectFormData['regions'][number] = {
      name: `Região do ${getValues('project_name')}`,
      removed_features: [],
      col_name: null,
    }

    const regions = getValues('regions')
    const existRegion = regions.findIndex(
      (region) => region.name === defaultRegion.name,
    )

    if (existRegion === -1) {
      setValue('regions', [...regions, defaultRegion])
    } else {
      regions[existRegion].removed = false

      setValue('regions', regions)
    }

    return defaultRegion
  }

  const errorMutation = (
    isEditProjectPage ? editProjectMutation.error : createProjectMutation.error
  ) as any

  const isLoadingMutation = isEditProjectPage
    ? editProjectMutation.isLoading
    : createProjectMutation.isLoading

  const isFetchingGetProject =
    isEditProjectPage && getProjectByIDQuery.isFetching

  const isSuccessMutation = isEditProjectPage
    ? editProjectMutation.isSuccess
    : createProjectMutation.isSuccess

  const projectId = isEditProjectPage
    ? projectIdFromPage
    : createProjectMutation.data?.id

  const isPrivateProject = getProjectByIDQuery.data?.private

  return {
    methods,
    activeStep,
    activeRegionName,
    goToFirstStep,
    goToSecondStep,
    goToBackRegion,
    goToNextRegion,
    setActiveRegionName,
    handleForm,
    isAlertModalOpen,
    closeAlertModal,
    errorMutation,
    isLoadingMutation,
    isFetchingGetProject,
    isSuccessMutation,
    isEditProjectPage,
    projectId,
    isPrivateProject,
  }
}

type ProjectContextData = Omit<ReturnType<typeof useProjectContext>, 'methods'>

export const ProjectContext = createContext({} as ProjectContextData)

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { methods, ...values } = useProjectContext()

  return (
    <FormProvider {...methods}>
      <ProjectContext.Provider value={values}>
        {children}
      </ProjectContext.Provider>
    </FormProvider>
  )
}

export function useProject() {
  const form = useFormContext<DefaultProjectFormData>()
  const context = useContext(ProjectContext)

  return {
    form,
    context,
  }
}
